/** biome-ignore-all lint/style/noMagicNumbers: Timing-related numbers */
import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { AlertCircle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Skeleton } from '@/shadcn/skeleton';

import { DiffViewer } from '@/components/monaco/diffViewer/diffViewer';
import { GET_TRACE_SNAPSHOT_BY_HASH } from '@/gql/queries/traceSnapshot';
import type { LinkQueryResult } from '../traceInspector.types';
import { LinkSelector } from './linkSelector';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  links: LinkQueryResult[];
  currentLinkHash?: string;
};

export const CompareLinksDiffModal = ({
  open,
  onOpenChange,
  links,
  currentLinkHash
}: Props) => {
  const [leftLinkHash, setLeftLinkHash] = useState<string | null>(null);
  const [rightLinkHash, setRightLinkHash] = useState<string | null>(null);

  // Initialize default selection when modal opens or links change
  useEffect(() => {
    if (!open || links.length === 0) return;

    // Sort links by height DESC (newest first)
    const sortedLinks = [...links].sort((a, b) => b.height - a.height);

    if (currentLinkHash) {
      // Find current link and select it + previous link
      const currentIndex = sortedLinks.findIndex(
        link => link.linkHash === currentLinkHash
      );
      if (currentIndex !== -1 && currentIndex < sortedLinks.length - 1) {
        setRightLinkHash(currentLinkHash);
        setLeftLinkHash(sortedLinks[currentIndex + 1].linkHash);
        return;
      }
    }

    // Default: select last two links (most recent + second most recent)
    if (sortedLinks.length >= 2) {
      setRightLinkHash(sortedLinks[0].linkHash);
      setLeftLinkHash(sortedLinks[1].linkHash);
    } else if (sortedLinks.length === 1) {
      // Only one link exists - select it for both
      setRightLinkHash(sortedLinks[0].linkHash);
      setLeftLinkHash(sortedLinks[0].linkHash);
    }
  }, [open, links, currentLinkHash]);

  // Get link heights for validation
  const leftLink = useMemo(
    () => links.find(link => link.linkHash === leftLinkHash),
    [links, leftLinkHash]
  );
  const rightLink = useMemo(
    () => links.find(link => link.linkHash === rightLinkHash),
    [links, rightLinkHash]
  );

  // Validation: left link height must be < right link height
  const isValidSelection = useMemo(() => {
    if (!(leftLink && rightLink)) return false;
    return leftLink.height < rightLink.height;
  }, [leftLink, rightLink]);

  // Query snapshots with retry logic - only when both links selected AND valid
  const {
    data: leftData,
    loading: leftLoading,
    error: leftError
  } = useQuery(GET_TRACE_SNAPSHOT_BY_HASH, {
    variables: { hash: leftLinkHash || '' },
    skip: !(leftLinkHash && rightLinkHash && isValidSelection),
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    context: {
      fetchOptions: {
        retries: 2, // Retry 2 more times (3 total attempts)
        retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000) // 1s, 2s
      }
    }
  });

  const {
    data: rightData,
    loading: rightLoading,
    error: rightError
  } = useQuery(GET_TRACE_SNAPSHOT_BY_HASH, {
    variables: { hash: rightLinkHash || '' },
    skip: !(leftLinkHash && rightLinkHash && isValidSelection),
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    context: {
      fetchOptions: {
        retries: 2,
        retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000)
      }
    }
  });

  // Function to determine if a link should be disabled in left selector
  const isLeftLinkDisabled = (linkHash: string): boolean => {
    if (!rightLink) return false;
    const link = links.find(l => l.linkHash === linkHash);
    if (!link) return false;
    // Disable if height >= right link height
    return link.height >= rightLink.height;
  };

  // Function to determine if a link should be disabled in right selector
  const isRightLinkDisabled = (linkHash: string): boolean => {
    if (!leftLink) return false;
    const link = links.find(l => l.linkHash === linkHash);
    if (!link) return false;
    // Disable if height <= left link height
    return link.height <= leftLink.height;
  };

  const renderDiffArea = () => {
    // Case 1: No selection or invalid selection
    if (!(leftLinkHash && rightLinkHash && isValidSelection)) {
      return (
        <div className="flex h-full items-center justify-center rounded-lg border bg-muted/20">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">
              {leftLinkHash && rightLinkHash
                ? 'Link 1 must be earlier than Link 2. Please adjust your selection.'
                : 'Select two links to view their diff'}
            </p>
          </div>
        </div>
      );
    }

    // Case 2: Loading
    const loading = leftLoading || rightLoading;
    if (loading) {
      return (
        <div className="flex h-full items-center justify-center rounded-lg border">
          <Skeleton className="h-full w-full" />
        </div>
      );
    }

    // Case 3: Error (after 3 retry attempts)
    const error = leftError || rightError;
    if (error) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-destructive bg-destructive/10 p-6 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <h3 className="font-semibold text-foreground">
                Error loading snapshots
              </h3>
              <p className="mt-2 text-muted-foreground text-sm">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Case 4: No snapshot data
    const leftSnapshot = leftData?.traceSnapshotByHash;
    const rightSnapshot = rightData?.traceSnapshotByHash;
    if (!(leftSnapshot && rightSnapshot)) {
      return (
        <div className="flex h-full items-center justify-center p-6">
          <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border bg-muted/50 p-6 text-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
            <div>
              <h3 className="font-semibold text-foreground">
                No snapshot data available
              </h3>
              <p className="mt-2 text-muted-foreground text-sm">
                One or both snapshots are not available. This may be because the
                feature is not enabled or snapshots have not been created yet.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Case 5: Success - Show DiffViewer
    const leftContent = {
      data: leftSnapshot.data,
      nextActions: leftSnapshot.nextActions,
      tasks: leftSnapshot.tasks,
      notifications: leftSnapshot.notifications
    };

    const rightContent = {
      data: rightSnapshot.data,
      nextActions: rightSnapshot.nextActions,
      tasks: rightSnapshot.tasks,
      notifications: rightSnapshot.notifications
    };

    return (
      <div className="h-full">
        <DiffViewer after={rightContent} before={leftContent} />
      </div>
    );
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex h-[90vh] max-w-[95vw] flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Compare Actions</DialogTitle>
          <DialogDescription>
            Select two actions and compare their data
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 flex-col gap-4 overflow-hidden">
          <div className="flex gap-4 px-6">
            <div className="flex-1">
              <LinkSelector
                isLinkDisabled={isLeftLinkDisabled}
                label="Left action"
                links={links}
                onSelectLink={setLeftLinkHash}
                selectedLinkHash={leftLinkHash}
              />
            </div>
            <div className="flex-1">
              <LinkSelector
                isLinkDisabled={isRightLinkDisabled}
                label="Right action"
                links={links}
                onSelectLink={setRightLinkHash}
                selectedLinkHash={rightLinkHash}
              />
            </div>
          </div>

          {/* Bottom section: Diff viewer (fills remaining space) */}
          <div className="min-h-0 flex-1">{renderDiffArea()}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
